import { useMemo } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormattedMessage, useIntl } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { map } from 'lodash'
import FormikTextField from '../../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../../shared/components/formik/FormikSelectField'
import FormikEditorField from '../../../../shared/components/formik/FormikEditorField'

const IngressControllerForm = ({ ingressController, services, networks, createIngressController, updateIngressController, onSuccess, onCancel }) => {
  const intl = useIntl()
  const isEditMode = Boolean(ingressController)

  const serviceOptions = useMemo(() => {
    return map(services, (service) => ({
      value: service.id,
      label: service.name
    }))
  }, [services])

  const networkOptions = useMemo(() => {
    return map(networks, (network) => ({
      value: network.id,
      label: network.name
    }))
  }, [networks])

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    serviceId: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    networkId: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    serviceExtensionLiquid: Yup.string(),
    projectExtensionLiquid: Yup.string()
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: ingressController?.name || '',
      serviceId: ingressController?.serviceId || '',
      networkId: ingressController?.networkId || '',
      serviceExtensionLiquid: ingressController?.serviceExtensionLiquid || '',
      projectExtensionLiquid: ingressController?.projectExtensionLiquid || ''
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const ingressControllerData = {
        ingressController: {
          name: values.name,
          serviceId: values.serviceId,
          networkId: values.networkId,
          serviceExtensionLiquid: values.serviceExtensionLiquid || null,
          projectExtensionLiquid: values.projectExtensionLiquid || null
        }
      }

      const apiCall = isEditMode
        ? updateIngressController(ingressController.id, ingressControllerData)
        : createIngressController(ingressControllerData)

      apiCall
        .then(() => {
          onSuccess()
        })
        .catch((err) => {
          setStatus(err.message || 'Error saving ingress controller')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <FormikTextField
        formik={formik}
        name="name"
        label={<FormattedMessage id="ingressControllers.create.nameLabel" />}
        autoFocus
      />

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="serviceId"
          label={<FormattedMessage id="ingressControllers.create.serviceLabel" />}
          options={serviceOptions}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikSelectField
          formik={formik}
          name="networkId"
          label={<FormattedMessage id="ingressControllers.create.networkLabel" />}
          options={networkOptions}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikEditorField
          formik={formik}
          name="serviceExtensionLiquid"
          label={<FormattedMessage id="ingressControllers.create.serviceExtensionLiquidLabel" />}
          language="yaml"
          height="150px"
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <FormikEditorField
          formik={formik}
          name="projectExtensionLiquid"
          label={<FormattedMessage id="ingressControllers.create.projectExtensionLiquidLabel" />}
          language="yaml"
          height="150px"
        />
      </Box>

      {formik.status && (
        <Typography color="error" sx={{ mt: 2 }}>
          {formik.status}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={formik.isSubmitting}
        >
          <FormattedMessage id={isEditMode ? 'ingressControllers.edit.submitButton' : 'ingressControllers.create.submitButton'} />
        </Button>
      </Box>
    </Box>
  )
}

export default IngressControllerForm
